/**
 * Keep the bottom of the header search panel on screen.
 *
 * The predictive results and the suggestions list scroll inside a box capped at
 * `max-h-[60vh]`, with "View all" (results) or bottom padding (suggestions)
 * below it. `vh` is the *large* viewport on iOS Safari, measured with the
 * toolbars collapsed, and the open panel locks page scroll. On a short phone
 * (iPhone 12 mini) that put "View all" under Safari's bottom toolbar, where
 * neither a swipe nor VoiceOver could reach it (FB-1556 QA).
 *
 * This shrinks the scroll box only when the page is scroll-locked and the panel
 * overruns the viewport, so the panel ends on screen and its list scrolls
 * instead. The viewport is the
 * smaller of `innerHeight` and `documentElement.clientHeight`: on iOS the
 * latter is the small viewport (toolbars shown), and neither shrinks for the
 * on-screen keyboard, so the list does not collapse while someone types.
 *
 * The results are re-rendered by Alpine's `x-html` on every search, and the
 * whole header block arrives by ajax, so scroll boxes are discovered with a
 * MutationObserver and then watched with a ResizeObserver, which also fires
 * when the suggestions panel is shown (its visibility is a class toggle).
 * Plain `defer`: it needs no Alpine and binds on parse.
 */
(() => {
  'use strict';

  const SCROLLER_SELECTOR = '[data-predictive-search] > div > .scrollbar-body';
  // Breathing room below the panel, and the smallest list worth showing.
  const GAP = 8;
  const MIN_HEIGHT = 120;

  const observed = new WeakSet();

  const viewportHeight = () =>
    Math.min(window.innerHeight, document.documentElement.clientHeight);

  // Only a scroll-locked page traps the panel's bottom off screen. The search
  // page's own in-page panel scrolls with the page, so it is left alone.
  const pageScrollLocked = () =>
    [document.documentElement, document.body].some((el) =>
      ['hidden', 'clip'].includes(getComputedStyle(el).overflowY),
    );

  const fit = (scroller) => {
    if (!scroller.isConnected) {
      return;
    }

    scroller.style.removeProperty('max-height');
    if (!pageScrollLocked()) {
      return;
    }

    const panel = scroller.parentElement;
    const scrollerRect = scroller.getBoundingClientRect();
    if (!panel || scrollerRect.height === 0) {
      return;
    }

    // Everything the panel renders below the scroll box: padding, "View all".
    const tail = panel.getBoundingClientRect().bottom - scrollerRect.bottom;
    const overrun = scrollerRect.bottom + tail + GAP - viewportHeight();
    if (overrun <= 0) {
      return;
    }

    const height = Math.max(scrollerRect.height - overrun, MIN_HEIGHT);
    scroller.style.maxHeight = `${Math.floor(height)}px`;
  };

  const resizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => fit(entry.target));
  });

  const watch = (root) => {
    root.querySelectorAll?.(SCROLLER_SELECTOR).forEach((scroller) => {
      if (observed.has(scroller)) {
        return;
      }
      observed.add(scroller);
      resizeObserver.observe(scroller);
    });
  };

  // Only added subtrees are inspected: a results render lands inside an
  // existing `[data-predictive-search]`, the ajax header swap brings one in.
  new MutationObserver((records) => {
    for (const record of records) {
      const host = record.target.closest?.('[data-predictive-search]');
      if (host) {
        watch(host);
        continue;
      }
      record.addedNodes.forEach((node) => {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches('[data-predictive-search]') ||
            node.querySelector('[data-predictive-search]'))
        ) {
          watch(node);
        }
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  watch(document);

  const refitAll = () => {
    document.querySelectorAll(SCROLLER_SELECTOR).forEach(fit);
  };
  window.addEventListener('resize', refitAll);
  window.visualViewport?.addEventListener('resize', refitAll);

  // The header panel closes with `visibility: hidden`, keeping its size, so no
  // resize is observed and a height fitted for the last open would be reused.
  // Opening it moves focus into the form; refit once its 300ms slide-in ends.
  let refitTimer;
  document.addEventListener('focusin', (event) => {
    if (!event.target.closest?.('form')?.querySelector('[data-predictive-search]')) {
      return;
    }
    clearTimeout(refitTimer);
    refitTimer = setTimeout(refitAll, 350);
  });
})();
